import { Alert, Button, Grid, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { Field, Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { TextField } from 'formik-mui';
import { omit, sortBy } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { cascadesApi } from 'api';
import {
  DataWrapper,
  FormActions,
  FormControls,
  FormikNumericField,
  FormikSelect,
  PageHeader,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey, RequisitesCascadeMode } from 'enums';
import { useMutation, useAppHistory, useMeta } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Cascade, OrderAssigneeType } from 'types';
import { validationUtils } from 'utils';

import { CascadeP2PProviders } from './CascadeP2PProviders';
import { CascadeTimeoutAssignees } from './CascadeTimeoutAssignees';

export type Values = Pick<
  Cascade,
  | 'name'
  | 'payinCascadeMode'
  | 'payoutCascadeMode'
  | 'p2pProvidersConfigs'
  | 'payoutP2PProvidersConfigs'
  | 'payoutOrderAssignees'
  | 'payoutOrderAssigneesTimeout'
>;

type CreateCascadePayload = Omit<
  Values,
  'p2pProvidersConfigs' | 'payoutP2PProvidersConfigs' | 'payoutOrderAssignees'
> & {
  p2pProvidersConfigs: Array<{
    p2pProviderId: string;
    priority: number;
  }>;
  payoutP2PProvidersConfigs: Array<{
    p2pProviderId: string;
    priority: number;
  }>;
  payoutOrderAssignees: Array<{
    assigneeType: OrderAssigneeType;
    p2pProviderId?: string;
    traderId?: string;
    priority: number;
    timeout: number;
  }>;
};

export const ManageCascade: React.FC = () => {
  const { id: cascadeId = '' } = useParams<{ id: string }>();
  const { goBack } = useAppHistory();
  const { isPayoutOrderCascadeTimeoutEnabled } = useMeta();

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.cascades.manage',
  });
  const { t: tCommon } = useTranslation();

  const isNew = useMemo(() => cascadeId === NEW_ID, [cascadeId]);

  const title = useMemo(
    () => t(isNew ? 'new_cascade.title' : 'edit_cascade.title'),
    [isNew, t],
  );

  const backUrl = useMemo(() => ROUTE_PATH.ADMIN.CASCADES, []);

  const initialState: Values = useMemo(
    () => ({
      name: '',
      payinCascadeMode: RequisitesCascadeMode.TradersFirst,
      payoutCascadeMode: RequisitesCascadeMode.TradersFirst,
      p2pProvidersConfigs: [],
      payoutP2PProvidersConfigs: [],
      payoutOrderAssignees: [],
      payoutOrderAssigneesTimeout: 0,
    }),
    [],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(tCommon('errors.required')),
        payinCascadeMode: Yup.string()
          .oneOf(Object.values(RequisitesCascadeMode))
          .required(tCommon('errors.required')),
        payoutCascadeMode: Yup.string()
          .oneOf(Object.values(RequisitesCascadeMode))
          .required(tCommon('errors.required')),
        p2pProvidersConfigs: Yup.array().of(
          Yup.object().shape({
            p2pProviderId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        payoutP2PProvidersConfigs: Yup.array().of(
          Yup.object().shape({
            p2pProviderId: Yup.string().required(tCommon('errors.required')),
          }),
        ),
        payoutOrderAssigneesTimeout: Yup.number().when('payoutCascadeMode', {
          is: RequisitesCascadeMode.SequentialAssignees,
          then: (schema) =>
            schema
              .required(tCommon('errors.required'))
              .min(1, tCommon('errors.more_than_min')),
        }),
        payoutOrderAssignees: Yup.array().of(
          Yup.object().shape({
            p2pProviderId: Yup.string()
              .nullable()
              .when('assigneeType', {
                is: OrderAssigneeType.P2PProvider,
                then: (schema) => schema.required(tCommon('errors.required')),
              }),
            traderId: Yup.string()
              .nullable()
              .when('assigneeType', {
                is: OrderAssigneeType.Trader,
                then: (schema) => schema.required(tCommon('errors.required')),
              }),
            timeout: Yup.number()
              .required(tCommon('errors.required'))
              .min(1, tCommon('errors.more_than_min')),
            assigneeType: Yup.string()
              .oneOf(Object.values(OrderAssigneeType))
              .required(tCommon('errors.required')),
          }),
        ),
      }),
    [tCommon],
  );

  const [initialValues, setInitialValues] = useState<Values>(initialState);

  const queryClient = useQueryClient();
  const queryResult = useQuery(
    [QueryKey.Cascades, cascadeId],
    () => cascadesApi.getOne(cascadeId),
    {
      enabled: !!cascadeId && !isNew,
      onSuccess: (data) => {
        setInitialValues({
          ...omit(data, 'createdAt', 'updatedAt', 'id'),
          p2pProvidersConfigs: sortBy(
            data.p2pProvidersConfigs || [],
            'priority',
          ),
          payoutP2PProvidersConfigs: sortBy(
            data.payoutP2PProvidersConfigs || [],
            'priority',
          ),
          payoutOrderAssignees: sortBy(
            data.payoutOrderAssignees || [],
            'priority',
          ),
        });
      },
      keepPreviousData: true,
    },
  );

  const { mutate: createCascade } = useMutation<
    Cascade,
    AxiosError,
    Partial<Cascade>
  >(cascadesApi.create);
  const { mutate: updateCascade } = useMutation<
    Cascade,
    AxiosError,
    { id: string; data: Partial<Cascade> }
  >(({ id, data }) => cascadesApi.update({ id, data }));

  const handleSubmit = useCallback(
    (cascade: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: () => {
          queryClient.invalidateQueries(QueryKey.Cascades);
          goBack(backUrl);
        },
        onError: (error: AxiosError) =>
          formikHelpers.setErrors(validationUtils.getFormErrors(error)),
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      formikHelpers.setSubmitting(true);

      const updatedCascade: CreateCascadePayload = {
        ...cascade,
        ...(cascade.payoutCascadeMode !==
          RequisitesCascadeMode.SequentialAssignees && {
          payoutOrderAssigneesTimeout: undefined,
        }),
        p2pProvidersConfigs: (cascade.p2pProvidersConfigs || []).map(
          (config, index) => ({
            p2pProviderId: config.p2pProviderId,
            priority: index,
          }),
        ),
        payoutP2PProvidersConfigs: (
          cascade.payoutP2PProvidersConfigs || []
        ).map((config, index) => ({
          p2pProviderId: config.p2pProviderId,
          priority: index,
        })),
        payoutOrderAssignees: (cascade.payoutOrderAssignees || []).map(
          (assignee, index) => ({
            ...(assignee.id && { id: assignee.id }),
            ...(assignee.assigneeType === OrderAssigneeType.Trader && {
              traderId: assignee.traderId,
            }),
            ...(assignee.assigneeType === OrderAssigneeType.P2PProvider && {
              p2pProviderId: assignee.p2pProviderId,
            }),
            priority: index,
            timeout: assignee.timeout,
            assigneeType: assignee.assigneeType,
          }),
        ),
      };

      if (isNew) {
        createCascade(updatedCascade as Partial<Cascade>, options);
      } else {
        updateCascade(
          { id: cascadeId, data: updatedCascade as Partial<Cascade> },
          options,
        );
      }
    },
    [
      isNew,
      queryClient,
      goBack,
      backUrl,
      createCascade,
      updateCascade,
      cascadeId,
    ],
  );

  const handleCancel = useCallback(() => goBack(backUrl), [goBack, backUrl]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  const assigneesTimeoutSum = useMemo(
    () =>
      (formik.values.payoutOrderAssignees || []).reduce(
        (sum, assignee) => sum + (assignee.timeout || 0),
        0,
      ),
    [formik.values.payoutOrderAssignees],
  );

  const showAssigneesTimeoutWarning = useMemo(
    () =>
      !!formik.values.payoutOrderAssigneesTimeout &&
      assigneesTimeoutSum > formik.values.payoutOrderAssigneesTimeout,
    [formik.values.payoutOrderAssigneesTimeout, assigneesTimeoutSum],
  );

  const payinModeOptions = useMemo(
    () => [
      {
        value: RequisitesCascadeMode.TradersFirst,
        label: t('modes.traders_first'),
      },
      {
        value: RequisitesCascadeMode.ProvidersFirst,
        label: t('modes.providers_first'),
      },
      {
        value: RequisitesCascadeMode.TradersOnly,
        label: t('modes.traders_only'),
      },
      {
        value: RequisitesCascadeMode.ProvidersOnly,
        label: t('modes.providers_only'),
      },
      {
        value: RequisitesCascadeMode.SequentialProviders,
        label: t('modes.sequential_providers'),
      },
      { value: RequisitesCascadeMode.Parallel, label: t('modes.parallel') },
    ],
    [t],
  );

  const payoutModeOptions = useMemo(
    () => [
      {
        value: RequisitesCascadeMode.TradersFirst,
        label: t('modes.default'), // renamed to avoid confusion
      },
      {
        value: RequisitesCascadeMode.TradersOnly,
        label: t('modes.traders_only'),
      },
      {
        value: RequisitesCascadeMode.ProvidersOnly,
        label: t('modes.providers_only'),
      },
      ...(isPayoutOrderCascadeTimeoutEnabled
        ? [
            {
              value: RequisitesCascadeMode.SequentialAssignees,
              label: t('modes.sequential_assignees'),
            },
          ]
        : []),
    ],
    [t, isPayoutOrderCascadeTimeoutEnabled],
  );

  return (
    <Fragment>
      <PageHeader title={title} />
      <FormikProvider value={formik}>
        <DataWrapper queryResult={!isNew ? queryResult : undefined}>
          <div className="tw-max-w-lg">
            <Form>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControls>
                    <Typography variant="subtitle1">
                      {t('sections.basic')}
                    </Typography>

                    <Field
                      component={TextField}
                      label={t('fields.name')}
                      name="name"
                      size="small"
                      required
                      fullWidth
                    />

                    <FormikSelect
                      label={t('fields.payin_cascade_mode')}
                      name="payinCascadeMode"
                      options={payinModeOptions}
                      required
                    />

                    <FormControls>
                      <CascadeP2PProviders
                        title={t('sections.p2p_providers.title')}
                      />
                    </FormControls>
                  </FormControls>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControls>
                    <FormikSelect
                      label={t('fields.payout_cascade_mode')}
                      name="payoutCascadeMode"
                      options={payoutModeOptions}
                      className="tw-mt-8"
                      required
                    />
                    {formik.values.payoutCascadeMode ===
                      RequisitesCascadeMode.SequentialAssignees && (
                      <Fragment>
                        <FormikNumericField
                          name="payoutOrderAssigneesTimeout"
                          label={t('fields.payout_order_assignees_timeout')}
                          min={1}
                          decimalScale={0}
                          allowNegative={false}
                          required
                          fullWidth
                        />
                        {showAssigneesTimeoutWarning && (
                          <Alert severity="warning">
                            <Typography variant="body2">
                              {t('alerts.assignees_timeout_warning')}
                            </Typography>
                          </Alert>
                        )}
                        <CascadeTimeoutAssignees />
                      </Fragment>
                    )}
                    {[
                      RequisitesCascadeMode.ProvidersOnly,
                      RequisitesCascadeMode.TradersFirst,
                    ].includes(formik.values.payoutCascadeMode) && (
                      <CascadeP2PProviders
                        title={tCommon(
                          'features.cascades.manage.sections.payout_p2p_providers.title',
                        )}
                        fieldName="payoutP2PProvidersConfigs"
                      />
                    )}
                  </FormControls>
                </Grid>
              </Grid>

              <FormActions>
                <Button variant="outlined" onClick={handleCancel}>
                  {tCommon('buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting || !formik.isValid}
                  onClick={formik.submitForm}
                >
                  {tCommon('buttons.save')}
                </Button>
              </FormActions>
            </Form>
          </div>
        </DataWrapper>
      </FormikProvider>
    </Fragment>
  );
};
